import { BasicInfoSection } from '@/components/InvoiceReportDetailDrawer/BasicInfoSection';
import { SECTION_IDS } from '@/components/InvoiceReportDetailDrawer/constants';
import { usePaymentDetail } from '@/components/InvoiceReportDetailDrawer/context/PaymentDetailContext';
import type { SectionCollapseStates } from '@/components/InvoiceReportDetailDrawer/hooks/useSectionCollapseStates';
import { useSectionToggles } from '@/components/InvoiceReportDetailDrawer/hooks/useSectionToggles';
import { GetAdminInvoiceReportSummaryResponse } from '@/context/services/admin_invoice_reports/AdminInvoiceReportSummary.service';
import { Loading } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback } from 'react';
import { ApplicationContent } from './ApplicationContent';
import { JournalInfo } from './JournalInfo';
import styles from './styles.module.scss';
import { TransactionsDetailSection } from './TransactionsDetail';

const cx = classnames.bind(styles);

export type LeftContentProps = {
  reportId: string;
  data?: GetAdminInvoiceReportSummaryResponse;
  isLoading?: boolean;
  sectionStates?: SectionCollapseStates;
  onSectionToggle?: (sectionId: string, isOpen: boolean) => void;
};

export const LeftContent: FC<LeftContentProps> = memo(
  ({ reportId, data, isLoading, sectionStates, onSectionToggle }) => {
    const { isRightPaneCollapsed } = usePaymentDetail();

    const isSectionOpen = useCallback(
      (sectionId: string) => !sectionStates?.[sectionId],
      [sectionStates]
    );

    const sectionToggles = useSectionToggles(onSectionToggle);

    if (isLoading) {
      return (
        <div className={cx('loading-container')}>
          <Loading size='large' color='default' />
        </div>
      );
    }

    return (
      <div
        className={cx('left-content', { 'full-width': isRightPaneCollapsed })}
      >
        <BasicInfoSection
          data={data}
          initialOpen={isSectionOpen(SECTION_IDS.basicInfo)}
          onToggle={sectionToggles.basicInfo}
        />
        <ApplicationContent
          reportId={reportId}
          initialOpen={isSectionOpen(SECTION_IDS.applicationContent)}
          onToggle={sectionToggles.applicationContent}
        />
        <TransactionsDetailSection
          reportId={reportId}
          initialOpen={isSectionOpen(SECTION_IDS.transactionsDetail)}
          onToggle={sectionToggles.transactionsDetail}
        />
        <JournalInfo
          reportId={reportId}
          initialOpen={isSectionOpen(SECTION_IDS.journalInfo)}
          onToggle={sectionToggles.journalInfo}
        />
      </div>
    );
  }
);

LeftContent.displayName = 'AdminInvoiceReportDetailLeftContent';
